// Firebase Firestore imports
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  writeBatch,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore'

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged,
  User
} from 'firebase/auth'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { auth, db, storage } from '../config/firebase'

// Firestore Collections
const COLLECTIONS = {
  USERS: 'users',
  STUDY_SESSIONS: 'studySessions',
  STUDY_PLANS: 'studyPlans',
  GOALS: 'goals',
  EXAMS: 'exams',
  QUESTIONS: 'questions',
  NOTES: 'notes',
  NOTE_FOLDERS: 'noteFolders'
}

// Safe Firestore operation wrapper with retry logic
const safeFirestoreOperation = async <T>(
  operation: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T | null> => {
  console.log('safeFirestoreOperation başlatıldı')
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`Firestore operasyonu deneniyor (${attempt}/${retries})`)
      const result = await operation()
      console.log('Firestore operasyonu başarılı:', result)
      return result
    } catch (error: any) {
      console.error(`Firestore operation failed (attempt ${attempt}/${retries}):`, error)
      console.error('Hata detayları:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      })
      
      // Don't retry on certain errors
      if (error?.code === 'permission-denied' || error?.code === 'not-found') {
        console.error('Yeniden denenmeyecek hata türü:', error?.code)
        throw error
      }
      
      // If this was the last attempt, throw the error
      if (attempt === retries) {
        console.error('Tüm denemeler başarısız, hata fırlatılıyor')
        throw error
      }
      
      // Wait before retrying
      console.log(`${delay * attempt}ms bekleniyor...`)
      await new Promise(resolve => setTimeout(resolve, delay * attempt))
    }
  }
  return null
}

// Initialize demo user in Firestore
const initializeDemoUserInFirestore = async () => {
  try {
    // First check if demo user already exists in Authentication
    const demoEmail = 'demo@lgs.com'
    const demoPassword = 'demo123'
    
    // Try to create demo user in Firebase Authentication if it doesn't exist
    try {
      await createUserWithEmailAndPassword(auth, demoEmail, demoPassword)
      console.log('✅ Demo user created in Firebase Authentication')
    } catch (authError: any) {
      if (authError.code === 'auth/email-already-in-use') {
        console.log('ℹ️ Demo user already exists in Firebase Authentication')
      } else {
        console.warn('⚠️ Could not create demo user in Authentication:', authError.message)
        return // Exit if we can't create the auth user
      }
    }
    
    // Get demo user UID without signing in
    let demoUser
    try {
      // Try to get user by email (this won't sign them in)
      const userCredential = await signInWithEmailAndPassword(auth, demoEmail, demoPassword)
      demoUser = userCredential.user
      console.log('✅ Demo user UID obtained:', demoUser.uid)
      // Immediately sign out to prevent auto-login
      await signOut(auth)
      console.log('✅ Demo user signed out after UID retrieval')
    } catch (signInError) {
      console.error('❌ Could not get demo user UID:', signInError)
      return
    }
    
    // Check if user data exists in Firestore
    const demoUserRef = doc(db, COLLECTIONS.USERS, demoUser.uid)
    const demoUserSnap = await getDoc(demoUserRef)
    
    if (!demoUserSnap.exists()) {
      const demoUserData = {
        email: 'demo@lgs.com',
        name: 'Demo Kullanıcı',
        avatar: '',
        level: 5,
        xp: 450,
        coins: 250,
        badges: ['first-login', 'study-streak'],
        grade: '8. Sınıf',
        school: 'Demo Okulu',
        targetScore: 450,
        role: 'demo',
        roles: ['demo', 'user'],
        permissions: ['read', 'view', 'study', 'practice', 'take_exam', 'view_stats', 'view_progress'],
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        preferences: {
          theme: 'system',
          language: 'tr',
          notifications: {
            email: true,
            push: true,
            sms: false
          },
          privacy: {
            profileVisible: true,
            showProgress: true,
            showStats: true
          }
        },
        subscription: {
          plan: 'premium',
          features: ['all-exams', 'study-timer', 'progress-tracking', 'ai-tutor']
        },
        stats: {
          totalStudyTime: 1250,
          totalExams: 25,
          averageScore: 85,
          currentStreak: 7,
          longestStreak: 15,
          totalQuestions: 500,
          correctAnswers: 425
        }
      }
      
      await safeFirestoreOperation(() => 
        setDoc(demoUserRef, demoUserData)
      )
      
      console.log('✅ Demo user data initialized in Firestore')
    } else {
      console.log('ℹ️ Demo user data already exists in Firestore')
    }
    
    // Demo user data initialized successfully
    console.log('✅ Demo user data initialized and ready for use')
    
  } catch (error) {
    console.error('❌ Failed to initialize demo user:', error)
  }
}

// Initialize demo user on module load - TEMPORARILY DISABLED
// if (typeof window !== 'undefined') {
//   // Wait a bit for Firebase to initialize, then create demo user
//   setTimeout(() => {
//     initializeDemoUserInFirestore()
//   }, 2000)
// }

// Types
export interface FirebaseUser {
  id: string
  email: string
  name: string
  avatar?: string
  level: number
  xp: number
  coins: number
  badges: string[]
  grade: string
  school: string
  targetScore: number
  createdAt: string
  lastLoginAt: string
  emailVerified?: boolean
  profileCompleted?: boolean
  roles?: string[]
  preferences: {
    theme: string
    language: string
    notifications: {
      email: boolean
      push: boolean
      sms: boolean
    }
    privacy: {
      profileVisible: boolean
      showProgress: boolean
      showStats: boolean
    }
  }
  subscription: {
    plan: string
    features: string[]
    startDate?: string
    endDate?: string | null
    isActive?: boolean
  }
  stats: {
    totalStudyTime: number
    totalExams: number
    averageScore: number
    currentStreak: number
    longestStreak: number
    totalQuestions: number
    correctAnswers: number
    completedLessons?: number
    achievements?: number
  }
}

export interface StudySession {
  id: string
  userId: string
  subject: string
  topic: string
  duration: number
  startTime: string
  endTime: string
  questionsAnswered: number
  correctAnswers: number
  score: number
  notes?: string
  createdAt: string
}

export interface StudyPlan {
  id: string
  userId: string
  title: string
  description?: string
  subjects: string[]
  startDate: string
  endDate: string
  dailyGoal: number
  weeklyGoal: number
  isActive: boolean
  progress: {
    completedDays: number
    totalDays: number
    completedHours: number
    targetHours: number
  }
  createdAt: string
  updatedAt: string
}

export interface Goal {
  id: string
  userId: string
  title: string
  description: string
  type: 'daily' | 'weekly' | 'monthly' | 'custom'
  target: number
  current: number
  unit: string
  deadline: string
  isCompleted: boolean
  reward?: {
    xp: number
    coins: number
    badge?: string
  }
  createdAt: string
  updatedAt: string
}

export interface FirebaseNote {
  id: string
  userId: string
  title: string
  content: string
  folderId?: string
  subjectId?: string
  topicId?: string
  tags: string[]
  color: string
  isPinned: boolean
  isArchived: boolean
  isFavorite: boolean
  attachments: {
    id: string
    name: string
    url: string
    type: 'image' | 'pdf' | 'link' | 'audio'
    size?: number
  }[]
  reminders: {
    id: string
    date: string
    message: string
    isCompleted: boolean
  }[]
  collaborators: {
    userId: string
    permission: 'view' | 'edit' | 'admin'
    addedAt: string
  }[]
  version: number
  lastEditedBy: string
  createdAt: string
  updatedAt: string
}

export interface FirebaseNoteFolder {
  id: string
  userId: string
  name: string
  description?: string
  color: string
  noteIds: string[]
  isShared: boolean
  createdAt: string
  updatedAt: string
}

// Authentication Services
// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser
}

export const authService = {
  // Get current user
  getCurrentUser: (): User | null => {
    return auth.currentUser
  },

  // Register new user
  register: async (email: string, password: string, userData: Partial<FirebaseUser>) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const user = userCredential.user

      // Update profile
      await updateProfile(user, {
        displayName: userData.name
      })

      // Send email verification
      await sendEmailVerification(user)

      // Create user document in Firestore
      const userDoc: Omit<FirebaseUser, 'id'> = {
        email: user.email!,
        name: userData.name || '',
        avatar: userData.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`,
        level: 1,
        xp: 0,
        coins: 100,
        badges: ['Yeni Başlayan'],
        grade: userData.grade || '',
        school: userData.school || '',
        targetScore: userData.targetScore || 400,
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        emailVerified: user.emailVerified,
        profileCompleted: false,
        preferences: {
          theme: 'system',
          language: 'tr',
          notifications: {
            email: true,
            push: true,
            sms: false
          },
          privacy: {
            profileVisible: true,
            showProgress: true,
            showStats: true
          }
        },
        subscription: {
          plan: 'free',
          features: ['basic-exams', 'study-timer', 'progress-tracking'],
          startDate: new Date().toISOString(),
          endDate: null,
          isActive: true
        },
        stats: {
          totalStudyTime: 0,
          totalExams: 0,
          averageScore: 0,
          currentStreak: 0,
          longestStreak: 0,
          totalQuestions: 0,
          correctAnswers: 0,
          completedLessons: 0,
          achievements: 1
        }
      }

      // Save user data to Firestore
      try {
        const userRef = doc(db, COLLECTIONS.USERS, user.uid)
        await safeFirestoreOperation(() => 
          setDoc(userRef, { ...userDoc, uid: user.uid })
        )
        console.log('✅ User data saved to Firestore')
      } catch (error) {
        console.warn('⚠️ Could not save user data to Firestore:', error)
      }

      return { user, userData: userDoc }
    } catch (error) {
      console.error('❌ Registration error:', error)
      throw error
    }
  },

  // Login user
  login: async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const user = userCredential.user

      // Update last login time in Firestore
      try {
        const userRef = doc(db, COLLECTIONS.USERS, user.uid)
        await safeFirestoreOperation(() => 
          updateDoc(userRef, { lastLoginAt: serverTimestamp() })
        )
        console.log('✅ Last login time updated in Firestore')
      } catch (error) {
        console.warn('⚠️ Could not update last login time:', error)
      }

      return user
    } catch (error) {
      console.error('❌ Login error:', error)
      throw error
    }
  },

  // Logout user
  logout: async () => {
    try {
      await signOut(auth)
    } catch (error) {
      console.error('Logout error:', error)
      throw error
    }
  },

  // Reset password
  resetPassword: async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (error) {
      console.error('Password reset error:', error)
      throw error
    }
  },

  // Update password
  updateUserPassword: async (newPassword: string) => {
    try {
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, newPassword)
      }
    } catch (error) {
      console.error('Password update error:', error)
      throw error
    }
  },

  // Reauthenticate user
  reauthenticate: async (email: string, password: string) => {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('No user is currently signed in')
      }
      
      const credential = EmailAuthProvider.credential(email, password)
      await reauthenticateWithCredential(user, credential)
    } catch (error) {
      console.error('Reauthentication error:', error)
      throw error
    }
  },

  // Listen to auth state changes
  onAuthStateChanged: (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, callback)
  }
}

// User Services
export const userService = {
  // Get user data
  getUserData: async (uid: string): Promise<FirebaseUser | null> => {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, uid)
      const userSnap = await safeFirestoreOperation(() => getDoc(userRef))
      
      if (userSnap && userSnap.exists()) {
        const userData = userSnap.data()
        
        // Convert Firestore Timestamps to serializable strings
        const serializedUserData = {
          ...userData,
          createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate().toISOString() : userData.createdAt,
          lastLoginAt: userData.lastLoginAt?.toDate ? userData.lastLoginAt.toDate().toISOString() : userData.lastLoginAt,
          updatedAt: userData.updatedAt?.toDate ? userData.updatedAt.toDate().toISOString() : userData.updatedAt
        }
        
        return { id: userSnap.id, ...serializedUserData } as FirebaseUser
      }
      
      return null
    } catch (error) {
      console.error('❌ Get user data error:', error)
      return null
    }
  },

  // Update user data
  updateUserData: async (uid: string, userData: Partial<FirebaseUser>) => {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, uid)
      await safeFirestoreOperation(() => 
        updateDoc(userRef, { 
          ...userData, 
          updatedAt: serverTimestamp() 
        })
      )
      console.log('✅ User data updated in Firestore')
    } catch (error) {
      console.error('❌ Update user data error:', error)
      throw error
    }
  },

  // Add XP to user
  addXP: async (uid: string, xpAmount: number) => {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, uid)
      
      // Use atomic increment for XP
      await safeFirestoreOperation(() => 
        updateDoc(userRef, {
          xp: increment(xpAmount),
          updatedAt: serverTimestamp()
        })
      )
      
      // Get updated user data to calculate new level
      const userData = await userService.getUserData(uid)
      if (userData) {
        const newLevel = Math.floor(userData.xp / 100) + 1
        if (newLevel !== userData.level) {
          await safeFirestoreOperation(() => 
            updateDoc(userRef, { level: newLevel })
          )
        }
      }
      
      console.log('✅ XP added successfully')
    } catch (error) {
      console.error('❌ Add XP error:', error)
      throw error
    }
  },

  // Add coins to user
  addCoins: async (uid: string, coinAmount: number) => {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, uid)
      
      // Use atomic increment for coins
      await safeFirestoreOperation(() => 
        updateDoc(userRef, {
          coins: increment(coinAmount),
          updatedAt: serverTimestamp()
        })
      )
      
      console.log('✅ Coins added successfully')
    } catch (error) {
      console.error('❌ Add coins error:', error)
      throw error
    }
  }
}

// Study Session Services
export const studySessionService = {
  // Create study session
  createSession: async (sessionData: Omit<StudySession, 'id' | 'createdAt'>) => {
    try {
      const docRef = await safeFirestoreOperation(() => 
        addDoc(collection(db, COLLECTIONS.STUDY_SESSIONS), {
          ...sessionData,
          createdAt: serverTimestamp()
        })
      )
      console.log('✅ Study session created')
      return docRef?.id || null
    } catch (error) {
      console.error('❌ Create study session error:', error)
      throw error
    }
  },

  // Get user study sessions
  getUserSessions: async (userId: string, limitCount = 20) => {
    try {
      const sessionsQuery = query(
        collection(db, COLLECTIONS.STUDY_SESSIONS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      )
      
      const snapshot = await safeFirestoreOperation(() => getDocs(sessionsQuery))
      if (snapshot) {
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StudySession))
      }
      return []
    } catch (error) {
      console.error('❌ Get user sessions error:', error)
      return []
    }
  },

  // Update study session
  updateSession: async (sessionId: string, sessionData: Partial<StudySession>) => {
    try {
      const sessionRef = doc(db, COLLECTIONS.STUDY_SESSIONS, sessionId)
      await safeFirestoreOperation(() => 
        updateDoc(sessionRef, {
          ...sessionData,
          updatedAt: serverTimestamp()
        })
      )
      console.log('✅ Study session updated')
    } catch (error) {
      console.error('❌ Update study session error:', error)
      throw error
    }
  }
}

// Study Plan Services
export const studyPlanService = {
  // Create study plan
  createPlan: async (planData: Omit<StudyPlan, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const docRef = await addDoc(collection(db, 'studyPlans'), {
        ...planData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      return docRef.id
    } catch (error) {
      console.error('Create study plan error:', error)
      throw error
    }
  },

  // Get user study plans
  getUserPlans: async (userId: string) => {
    try {
      const plansQuery = query(
        collection(db, 'studyPlans'),
        where('userId', '==', userId)
      )
      
      const snapshot = await getDocs(plansQuery)
      const plans = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StudyPlan))
      // Index gerektirmeden istemci tarafında tarihe göre sırala
      plans.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
      return plans
    } catch (error) {
      console.error('Get user plans error:', error)
      throw error
    }
  },

  // Update study plan
  updatePlan: async (planId: string, planData: Partial<StudyPlan>) => {
    try {
      if (!planId) {
        throw new Error('Plan ID is required for update operation')
      }
      const planRef = doc(db, 'studyPlans', planId)
      await updateDoc(planRef, {
        ...planData,
        updatedAt: new Date().toISOString()
      })
    } catch (error) {
      console.error('Update study plan error:', error)
      throw error
    }
  },

  // Delete study plan
  deletePlan: async (planId: string) => {
    try {
      const planRef = doc(db, 'studyPlans', planId)
      await deleteDoc(planRef)
    } catch (error) {
      console.error('Delete study plan error:', error)
      throw error
    }
  }
}

// Goal Services
export const goalService = {
  // Create goal
  createGoal: async (goalData: Omit<Goal, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const docRef = await addDoc(collection(db, 'goals'), {
        ...goalData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      return docRef.id
    } catch (error) {
      console.error('Create goal error:', error)
      throw error
    }
  },

  // Get user goals
  getUserGoals: async (userId: string) => {
    try {
      const goalsQuery = query(
        collection(db, 'goals'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      )
      
      const snapshot = await getDocs(goalsQuery)
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Goal))
    } catch (error) {
      console.error('Get user goals error:', error)
      throw error
    }
  },

  // Update goal
  updateGoal: async (goalId: string, goalData: Partial<Goal>) => {
    try {
      const goalRef = doc(db, 'goals', goalId)
      await updateDoc(goalRef, {
        ...goalData,
        updatedAt: new Date().toISOString()
      })
    } catch (error) {
      console.error('Update goal error:', error)
      throw error
    }
  },

  // Delete goal
  deleteGoal: async (goalId: string) => {
    try {
      const goalRef = doc(db, 'goals', goalId)
      await deleteDoc(goalRef)
    } catch (error) {
      console.error('Delete goal error:', error)
      throw error
    }
  },

  // Complete goal
  completeGoal: async (goalId: string, userId: string) => {
    try {
      const goalRef = doc(db, 'goals', goalId)
      const goalDoc = await getDoc(goalRef)
      
      if (goalDoc.exists()) {
        const goalData = goalDoc.data() as Goal
        
        // Update goal as completed
        await updateDoc(goalRef, {
          isCompleted: true,
          updatedAt: new Date().toISOString()
        })

        // Add rewards if any
        if (goalData.reward) {
          if (goalData.reward.xp) {
            await userService.addXP(userId, goalData.reward.xp)
          }
          if (goalData.reward.coins) {
            await userService.addCoins(userId, goalData.reward.coins)
          }
        }
      }
    } catch (error) {
      console.error('Complete goal error:', error)
      throw error
    }
  }
}

// Storage Services
export const storageService = {
  // Upload file
  uploadFile: async (file: File, path: string) => {
    try {
      const fileRef = ref(storage, path)
      const snapshot = await uploadBytes(fileRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)
      return downloadURL
    } catch (error) {
      console.error('Upload file error:', error)
      throw error
    }
  },

  // Delete file
  deleteFile: async (path: string) => {
    try {
      const fileRef = ref(storage, path)
      await deleteObject(fileRef)
    } catch (error) {
      console.error('Delete file error:', error)
      throw error
    }
  }
}

// Notes Services
export const noteService = {
  // Create a new note
  createNote: async (noteData: Omit<FirebaseNote, 'id' | 'createdAt' | 'updatedAt'>) => {
    console.log('noteService.createNote çağrıldı:', noteData)
    return await safeFirestoreOperation(async () => {
      console.log('Firestore\'a not ekleniyor...')
      
      // Undefined değerleri filtrele
      const cleanedData = Object.fromEntries(
        Object.entries(noteData).filter(([_, value]) => value !== undefined)
      )
      console.log('Temizlenmiş veri:', cleanedData)
      
      const docRef = await addDoc(collection(db, COLLECTIONS.NOTES), {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      console.log('Not Firestore\'a eklendi, ID:', docRef.id)
      return docRef.id
    })
  },

  // Get user's notes
  getUserNotes: async (userId: string) => {
    return await safeFirestoreOperation(async () => {
      const notesQuery = query(
        collection(db, COLLECTIONS.NOTES),
        where('userId', '==', userId),
        where('isArchived', '==', false),
        orderBy('updatedAt', 'desc')
      )
      const snapshot = await getDocs(notesQuery)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseNote[]
    })
  },

  // Get archived notes
  getArchivedNotes: async (userId: string) => {
    return await safeFirestoreOperation(async () => {
      const notesQuery = query(
        collection(db, COLLECTIONS.NOTES),
        where('userId', '==', userId),
        where('isArchived', '==', true),
        orderBy('updatedAt', 'desc')
      )
      const snapshot = await getDocs(notesQuery)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseNote[]
    })
  },

  // Update a note
  updateNote: async (noteId: string, noteData: Partial<FirebaseNote>) => {
    console.log('noteService.updateNote çağrıldı:', { noteId, noteData })
    return await safeFirestoreOperation(async () => {
      console.log('Firestore\'da not güncelleniyor...')
      
      // Undefined değerleri filtrele
      const cleanedData = Object.fromEntries(
        Object.entries(noteData).filter(([_, value]) => value !== undefined)
      )
      console.log('Temizlenmiş güncelleme verisi:', cleanedData)
      
      const noteRef = doc(db, COLLECTIONS.NOTES, noteId)
      await updateDoc(noteRef, {
        ...cleanedData,
        updatedAt: serverTimestamp(),
        version: increment(1)
      })
      console.log('Not Firestore\'da güncellendi:', noteId)
      return true
    })
  },

  // Delete a note
  deleteNote: async (noteId: string) => {
    return await safeFirestoreOperation(async () => {
      const noteRef = doc(db, COLLECTIONS.NOTES, noteId)
      await deleteDoc(noteRef)
      return true
    })
  },

  // Toggle note pinned status
  toggleNotePinned: async (noteId: string, isPinned: boolean) => {
    return await safeFirestoreOperation(async () => {
      const noteRef = doc(db, COLLECTIONS.NOTES, noteId)
      await updateDoc(noteRef, {
        isPinned,
        updatedAt: serverTimestamp()
      })
      return true
    })
  },

  // Toggle note favorite status
  toggleNoteFavorite: async (noteId: string, isFavorite: boolean) => {
    return await safeFirestoreOperation(async () => {
      const noteRef = doc(db, COLLECTIONS.NOTES, noteId)
      await updateDoc(noteRef, {
        isFavorite,
        updatedAt: serverTimestamp()
      })
      return true
    })
  },

  // Toggle note archived status
  toggleNoteArchived: async (noteId: string, isArchived: boolean) => {
    return await safeFirestoreOperation(async () => {
      const noteRef = doc(db, COLLECTIONS.NOTES, noteId)
      await updateDoc(noteRef, {
        isArchived,
        updatedAt: serverTimestamp()
      })
      return true
    })
  },

  // Add tag to note
  addNoteTag: async (noteId: string, tag: string) => {
    return await safeFirestoreOperation(async () => {
      const noteRef = doc(db, COLLECTIONS.NOTES, noteId)
      await updateDoc(noteRef, {
        tags: arrayUnion(tag),
        updatedAt: serverTimestamp()
      })
      return true
    })
  },

  // Remove tag from note
  removeNoteTag: async (noteId: string, tag: string) => {
    return await safeFirestoreOperation(async () => {
      const noteRef = doc(db, COLLECTIONS.NOTES, noteId)
      await updateDoc(noteRef, {
        tags: arrayRemove(tag),
        updatedAt: serverTimestamp()
      })
      return true
    })
  }
}

// Note Folders Services
export const noteFolderService = {
  // Create a new folder
  createFolder: async (folderData: Omit<FirebaseNoteFolder, 'id' | 'createdAt' | 'updatedAt'>) => {
    return await safeFirestoreOperation(async () => {
      const docRef = await addDoc(collection(db, COLLECTIONS.NOTE_FOLDERS), {
        ...folderData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return docRef.id
    })
  },

  // Get user's folders
  getUserFolders: async (userId: string) => {
    return await safeFirestoreOperation(async () => {
      const foldersQuery = query(
        collection(db, COLLECTIONS.NOTE_FOLDERS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(foldersQuery)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseNoteFolder[]
    })
  },

  // Update a folder
  updateFolder: async (folderId: string, folderData: Partial<FirebaseNoteFolder>) => {
    return await safeFirestoreOperation(async () => {
      const folderRef = doc(db, COLLECTIONS.NOTE_FOLDERS, folderId)
      await updateDoc(folderRef, {
        ...folderData,
        updatedAt: serverTimestamp()
      })
      return true
    })
  },

  // Delete a folder
  deleteFolder: async (folderId: string) => {
    return await safeFirestoreOperation(async () => {
      const folderRef = doc(db, COLLECTIONS.NOTE_FOLDERS, folderId)
      await deleteDoc(folderRef)
      return true
    })
  },

  // Add note to folder
  addNoteToFolder: async (folderId: string, noteId: string) => {
    return await safeFirestoreOperation(async () => {
      const folderRef = doc(db, COLLECTIONS.NOTE_FOLDERS, folderId)
      await updateDoc(folderRef, {
        noteIds: arrayUnion(noteId),
        updatedAt: serverTimestamp()
      })
      return true
    })
  },

  // Remove note from folder
  removeNoteFromFolder: async (folderId: string, noteId: string) => {
    return await safeFirestoreOperation(async () => {
      const folderRef = doc(db, COLLECTIONS.NOTE_FOLDERS, folderId)
      await updateDoc(folderRef, {
        noteIds: arrayRemove(noteId),
        updatedAt: serverTimestamp()
      })
      return true
    })
  }
}

// Real-time listeners
export const realtimeService = {
  // Listen to user data changes
  listenToUserData: (uid: string, callback: (userData: FirebaseUser | null) => void) => {
    const userDocRef = doc(db, COLLECTIONS.USERS, uid)
    
    return onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const userData = { id: doc.id, ...doc.data() } as FirebaseUser
        callback(userData)
      } else {
        callback(null)
      }
    }, (error) => {
      console.error('Error listening to user data:', error)
      callback(null)
    })
  },

  // Listen to study sessions
  listenToStudySessions: (userId: string, callback: (sessions: StudySession[]) => void) => {
    const sessionsQuery = query(
      collection(db, COLLECTIONS.STUDY_SESSIONS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(20)
    )
    
    return onSnapshot(sessionsQuery, (snapshot) => {
      const sessions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as StudySession[]
      callback(sessions)
    })
  },

  // Listen to goals
  listenToGoals: (userId: string, callback: (goals: Goal[]) => void) => {
    const goalsQuery = query(
      collection(db, 'goals'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )
    
    return onSnapshot(goalsQuery, (snapshot) => {
      const goals = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Goal[]
      callback(goals)
    })
  },

  // Listen to notes
  listenToNotes: (userId: string, callback: (notes: FirebaseNote[]) => void) => {
    const notesQuery = query(
      collection(db, COLLECTIONS.NOTES),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    )
    
    return onSnapshot(notesQuery, (snapshot) => {
      const notes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseNote[]
      callback(notes)
    })
  },

  // Listen to note folders
  listenToNoteFolders: (userId: string, callback: (folders: FirebaseNoteFolder[]) => void) => {
    const foldersQuery = query(
      collection(db, COLLECTIONS.NOTE_FOLDERS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )
    
    return onSnapshot(foldersQuery, (snapshot) => {
      const folders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseNoteFolder[]
      callback(folders)
    })
  }
}
